import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Gift, Medal, Search, Loader2, Crown, Zap, Star, RotateCcw, XCircle, Clock, History, Ticket, Link2, Megaphone, ShieldAlert, KeyRound, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { fetchAdminUsuarios } from "../lib/admin";
import { otorgarLicenciaAdmin, actualizarPreciosPrograma, fetchProgramasLicenciablesAdmin, fetchLicenciasHistorialAdmin } from "../lib/licenciasAdmin";
import { LicenciaActual, GestorLicenciaModal } from "../components/admin/GestorLicencia";
import {
  fetchPromoResumen, fetchPromoHistorial, otorgarCortesia, convertirEmbajador,
  renovarBeneficio, finalizarBeneficio,
} from "../lib/promociones";

const GOLD = "#B45309";
const DURACIONES = [30, 60, 90];
const MOTIVOS = ["Embajador ConstruLink", "Alianza Estratégica", "Influencer", "Universidad", "Empresa", "Cliente Premium", "Cortesía Comercial", "Otro"];

const TABS = [
  { key: "cortesia", label: "Membresías de Cortesía", Icon: Gift, activo: true },
  { key: "embajadores", label: "Embajadores ConstruLink", Icon: Medal, activo: true },
  { key: "licencias", label: "Licencias de Programa", Icon: KeyRound, activo: true },
  { key: "historial", label: "Historial de Beneficios", Icon: History, activo: true },
  { key: "codigos", label: "Códigos Promocionales", Icon: Ticket, activo: false },
  { key: "enlaces", label: "Enlaces de Invitación", Icon: Link2, activo: false },
  { key: "campanas", label: "Campañas", Icon: Megaphone, activo: false },
];

function PlanChip({ plan }) {
  const c = plan === "elite" ? { l: "Premium", Icon: Crown, col: "#B45309" } : plan === "pro" ? { l: "Pro", Icon: Zap, col: "#2563EB" } : { l: "Básico", Icon: Star, col: "#64748B" };
  const { Icon } = c;
  return <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ color: c.col, backgroundColor: `${c.col}1a` }}><Icon size={11} /> {c.l}</span>;
}

function OrigenChip({ origen, label }) {
  const col = origen === "EMBAJADOR" ? "#B45309" : origen === "CORTESIA" ? "#7E22CE" : "#0891B2";
  return <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" style={{ color: col, backgroundColor: `${col}1a` }}>{label || origen}</span>;
}

function EstadoChip({ vigente, estado }) {
  const activo = vigente || estado === "activo";
  return <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${activo ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{activo ? "Activo" : "Finalizado"}</span>;
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-PE", { day: "2-digit", month: "short", year: "numeric" });
}

// Buscador de usuario reutilizando /admin/usuarios
function BuscadorUsuario({ onSelect, seleccionado, testid }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setRes([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const d = await fetchAdminUsuarios({ q, page_size: 8 }); setRes(d.items || []); }
      catch (_) { /* noop */ } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  if (seleccionado) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`${testid}-selected`}>
        <div className="min-w-0">
          <p className="font-bold text-slate-900 truncate">{seleccionado.name || seleccionado.email}</p>
          <p className="text-xs text-slate-500 truncate">{seleccionado.email} · {seleccionado.role}</p>
        </div>
        <button onClick={() => onSelect(null)} className="text-xs font-bold text-brand-red hover:underline" data-testid={`${testid}-clear`}>Cambiar</button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)}
          data-testid={`${testid}-input`}
          placeholder="Busca por nombre o correo…"
          className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none text-sm"
        />
        {loading && <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
      </div>
      {res.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white rounded-xl border border-slate-100 shadow-lifted max-h-64 overflow-y-auto" data-testid={`${testid}-results`}>
          {res.map((u) => (
            <button key={u.id} onClick={() => { onSelect(u); setQ(""); setRes([]); }} data-testid={`${testid}-opt-${u.id}`}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 border-b border-slate-50 last:border-0">
              <p className="font-semibold text-slate-800 text-sm truncate">{u.name || u.email}</p>
              <p className="text-xs text-slate-500 truncate">{u.email} · {u.role}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Modal de conflicto: ya existe un beneficio activo
function ConflictoModal({ activo, onRenovar, onReemplazar, onCancelar, busy }) {
  if (!activo) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="promo-conflicto-modal">
      <div className="bg-white rounded-2xl shadow-lifted max-w-md w-full p-6">
        <div className="flex items-center gap-2 text-amber-600"><ShieldAlert size={20} /><h3 className="font-bold text-slate-900">Beneficio activo existente</h3></div>
        <p className="text-sm text-slate-600 mt-3">
          {activo.user_nombre || "El usuario"} ya tiene un beneficio <b>{activo.origen_label}</b> ({activo.plan?.toUpperCase()}) activo hasta el <b>{fmt(activo.fecha_vencimiento)}</b>. No se permiten dos beneficios activos a la vez.
        </p>
        <p className="text-sm text-slate-500 mt-2">¿Qué deseas hacer?</p>
        <div className="mt-5 flex flex-col gap-2">
          <button onClick={onRenovar} disabled={busy} data-testid="promo-conflicto-renovar" className="w-full py-2.5 rounded-xl font-bold text-sm text-white bg-brand-blue hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2"><RotateCcw size={15} /> Renovar el beneficio actual</button>
          <button onClick={onReemplazar} disabled={busy} data-testid="promo-conflicto-reemplazar" className="w-full py-2.5 rounded-xl font-bold text-sm text-white hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2" style={{ backgroundColor: GOLD }}>Reemplazar por el nuevo</button>
          <button onClick={onCancelar} disabled={busy} data-testid="promo-conflicto-cancelar" className="w-full py-2.5 rounded-xl font-bold text-sm text-slate-600 border border-slate-200 hover:bg-slate-50">Esperar a que finalice (cancelar)</button>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 flex items-center gap-3" data-testid={`promo-kpi-${label}`}>
      <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}1a`, color }}><Icon size={20} /></span>
      <div><p className="text-2xl font-extrabold text-slate-900 leading-none">{value}</p><p className="text-xs text-slate-500 mt-1">{label}</p></div>
    </div>
  );
}

export default function AdminPromociones() {
  const { user } = useAuth();
  const [tab, setTab] = useState("cortesia");
  const [resumen, setResumen] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const recargar = () => setRefreshKey((k) => k + 1);

  useEffect(() => { fetchPromoResumen().then(setResumen).catch(() => {}); }, [refreshKey]);

  if (user && user.role !== "admin") {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="promo-denegado">
        <ShieldAlert size={40} className="mx-auto text-brand-red" />
        <h2 className="mt-4 text-xl font-bold text-slate-900">Acceso restringido</h2>
        <Link to="/" className="mt-4 inline-block text-brand-blue font-semibold hover:underline">Volver al inicio</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-promociones-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4"><ChevronLeft size={16} /> Panel Admin</Link>
      <header className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${GOLD}1a`, color: GOLD }}><Gift size={22} /></span>
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Beneficios Promocionales</h1>
          <p className="text-sm text-slate-500">Membresías especiales sin Mercado Pago · trazabilidad y auditoría completas.</p>
        </div>
      </header>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <KpiCard Icon={Gift} label="Cortesías activas" value={resumen?.cortesias_activas ?? "—"} color="#7E22CE" />
        <KpiCard Icon={Medal} label="Embajadores activos" value={resumen?.embajadores_activos ?? "—"} color={GOLD} />
        <KpiCard Icon={History} label="Total otorgados" value={resumen?.total_otorgados ?? "—"} color="#2563EB" />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-100 pb-3">
        {TABS.map((t) => {
          const Icon = t.Icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => t.activo && setTab(t.key)} disabled={!t.activo}
              data-testid={`promo-tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-bold transition-colors ${active ? "text-white" : t.activo ? "text-slate-600 hover:bg-slate-100" : "text-slate-400 cursor-not-allowed"}`}
              style={active ? { backgroundColor: GOLD } : {}}>
              <Icon size={15} /> {t.label}
              {!t.activo && <span className="text-[9px] font-bold uppercase bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded-full ml-1">Próximamente</span>}
            </button>
          );
        })}
      </div>

      {tab === "cortesia" && <CortesiaTab onDone={recargar} />}
      {tab === "embajadores" && <EmbajadoresTab onDone={recargar} refreshKey={refreshKey} />}
      {tab === "licencias" && <LicenciasProgramaTab />}
      {tab === "historial" && <HistorialTab onDone={recargar} refreshKey={refreshKey} />}
      {["codigos", "enlaces", "campanas"].includes(tab) && (
        <div className="text-center py-16 text-slate-400" data-testid="promo-proximamente">
          <Clock size={30} className="mx-auto text-slate-300" />
          <p className="mt-3 font-semibold text-slate-500">Funcionalidad en desarrollo</p>
          <p className="text-sm">Se habilitará dentro de este mismo módulo, sin reorganizar el panel.</p>
        </div>
      )}
    </div>
  );
}

function CortesiaTab({ onDone }) {
  const [usuario, setUsuario] = useState(null);
  const [plan, setPlan] = useState("pro");
  const [duracion, setDuracion] = useState(30);
  const [personalizado, setPersonalizado] = useState("");
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [busy, setBusy] = useState(false);
  const [conflicto, setConflicto] = useState(null);

  const diasFinal = duracion === "custom" ? parseInt(personalizado || "0", 10) : duracion;

  async function activar(reemplazar = false) {
    if (!usuario) { toast.error("Selecciona un usuario."); return; }
    if (!diasFinal || diasFinal < 1) { toast.error("Indica una duración válida."); return; }
    setBusy(true);
    try {
      const r = await otorgarCortesia({ user_id: usuario.id, plan, duracion_dias: diasFinal, motivo, reemplazar });
      if (r.conflict) { setConflicto(r.conflict); return; }
      toast.success("Membresía de cortesía activada.");
      setConflicto(null); setUsuario(null); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo activar."); }
    finally { setBusy(false); }
  }

  async function renovarActual() {
    if (!conflicto) return;
    setBusy(true);
    try { await renovarBeneficio(conflicto.id); toast.success("Beneficio renovado."); setConflicto(null); setUsuario(null); onDone(); }
    catch (e) { toast.error("No se pudo renovar."); } finally { setBusy(false); }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 max-w-2xl" data-testid="promo-cortesia-tab">
      <h2 className="font-bold text-slate-900 mb-4">Otorgar Membresía de Cortesía</h2>
      <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Usuario</label>
      <BuscadorUsuario onSelect={setUsuario} seleccionado={usuario} testid="cortesia-user" />

      <div className="grid grid-cols-2 gap-4 mt-5">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Plan</label>
          <select value={plan} onChange={(e) => setPlan(e.target.value)} data-testid="cortesia-plan" className="w-full py-2.5 px-3 rounded-xl border border-slate-200 text-sm">
            <option value="pro">Pro</option>
            <option value="elite">Premium</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Duración</label>
          <select value={duracion} onChange={(e) => setDuracion(e.target.value === "custom" ? "custom" : parseInt(e.target.value, 10))} data-testid="cortesia-duracion" className="w-full py-2.5 px-3 rounded-xl border border-slate-200 text-sm">
            {DURACIONES.map((d) => <option key={d} value={d}>{d} días</option>)}
            <option value="custom">Personalizado</option>
          </select>
        </div>
      </div>
      {duracion === "custom" && (
        <input type="number" min="1" value={personalizado} onChange={(e) => setPersonalizado(e.target.value)} data-testid="cortesia-dias-custom"
          placeholder="Número de días" className="mt-3 w-full py-2.5 px-3 rounded-xl border border-slate-200 text-sm" />
      )}

      <div className="mt-4">
        <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Motivo</label>
        <select value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="cortesia-motivo" className="w-full py-2.5 px-3 rounded-xl border border-slate-200 text-sm">
          {MOTIVOS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <button onClick={() => activar(false)} disabled={busy} data-testid="cortesia-activar"
        className="mt-6 w-full py-3 rounded-xl text-white font-bold inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: GOLD }}>
        {busy ? <Loader2 size={17} className="animate-spin" /> : <Gift size={17} />} Activar Membresía
      </button>

      <ConflictoModal activo={conflicto} busy={busy}
        onRenovar={renovarActual}
        onReemplazar={() => activar(true)}
        onCancelar={() => setConflicto(null)} />
    </div>
  );
}

function EmbajadoresTab({ onDone, refreshKey }) {
  const [usuario, setUsuario] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conflicto, setConflicto] = useState(null);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchPromoHistorial({ origen: "EMBAJADOR", estado: "activo", page_size: 100 })
      .then((d) => setLista(d.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, [refreshKey]);

  async function convertir(reemplazar = false) {
    if (!usuario) { toast.error("Selecciona un usuario."); return; }
    setBusy(true);
    try {
      const r = await convertirEmbajador({ user_id: usuario.id, reemplazar });
      if (r.conflict) { setConflicto(r.conflict); return; }
      toast.success("Usuario convertido en Embajador (Pro · 30 días).");
      setConflicto(null); setUsuario(null); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo convertir."); }
    finally { setBusy(false); }
  }

  async function renovarActual() {
    if (!conflicto) return;
    setBusy(true);
    try { await renovarBeneficio(conflicto.id); toast.success("Beneficio renovado."); setConflicto(null); setUsuario(null); onDone(); }
    catch (_) { toast.error("No se pudo renovar."); } finally { setBusy(false); }
  }

  async function accion(fn, id, msg) {
    try { await fn(id); toast.success(msg); onDone(); } catch (_) { toast.error("Acción fallida."); }
  }

  return (
    <div className="space-y-6" data-testid="promo-embajadores-tab">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 max-w-2xl">
        <h2 className="font-bold text-slate-900 mb-1">Convertir en Embajador</h2>
        <p className="text-xs text-slate-500 mb-4">Asigna automáticamente Membresía Pro por 30 días + insignia 🏅. No se renueva sola: la renovación es manual.</p>
        <BuscadorUsuario onSelect={setUsuario} seleccionado={usuario} testid="embajador-user" />
        <button onClick={() => convertir(false)} disabled={busy} data-testid="embajador-convertir"
          className="mt-5 w-full py-3 rounded-xl text-white font-bold inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: GOLD }}>
          {busy ? <Loader2 size={17} className="animate-spin" /> : <Medal size={17} />} Convertir en Embajador
        </button>
      </div>

      <div>
        <h3 className="font-bold text-slate-900 mb-3">Embajadores activos</h3>
        {loading ? <div className="text-slate-400 py-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>
          : lista.length === 0 ? <p className="text-sm text-slate-400 py-6 text-center bg-white rounded-2xl border border-slate-100">Aún no hay embajadores activos.</p>
          : (
            <div className="space-y-2">
              {lista.map((b) => (
                <div key={b.id} className="bg-white rounded-xl border border-slate-100 shadow-soft p-4 flex items-center justify-between gap-3" data-testid={`promo-embajador-${b.id}`}>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 truncate">{b.user_nombre || b.user_email}</p>
                    <p className="text-xs text-slate-500">Vence {fmt(b.fecha_vencimiento)} · {b.dias_restantes} días restantes</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => accion(renovarBeneficio, b.id, "Embajador renovado 30 días.")} data-testid={`promo-embajador-renovar-${b.id}`} className="text-xs font-bold text-brand-blue border border-brand-blue/30 rounded-lg px-3 py-1.5 hover:bg-brand-blue/5 inline-flex items-center gap-1"><RotateCcw size={13} /> Renovar</button>
                    <button onClick={() => accion(finalizarBeneficio, b.id, "Programa finalizado.")} data-testid={`promo-embajador-finalizar-${b.id}`} className="text-xs font-bold text-brand-red border border-brand-red/30 rounded-lg px-3 py-1.5 hover:bg-brand-red/5 inline-flex items-center gap-1"><XCircle size={13} /> Finalizar</button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      <ConflictoModal activo={conflicto} busy={busy}
        onRenovar={renovarActual} onReemplazar={() => convertir(true)} onCancelar={() => setConflicto(null)} />
    </div>
  );
}

function HistorialTab({ onDone, refreshKey }) {
  const [origen, setOrigen] = useState("");
  const [estado, setEstado] = useState("");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [lics, setLics] = useState([]);
  const [licRefresh, setLicRefresh] = useState(0);
  const [gestor, setGestor] = useState(null);

  useEffect(() => {
    fetchLicenciasHistorialAdmin().then((d) => setLics(d.items || [])).catch(() => setLics([]));
  }, [refreshKey, licRefresh]);

  useEffect(() => {
    setLoading(true);
    fetchPromoHistorial({ origen, estado, q, page_size: 50 })
      .then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [origen, estado, q, refreshKey]);

  async function accion(fn, id, msg) {
    try { await fn(id); toast.success(msg); onDone(); } catch (_) { toast.error("Acción fallida."); }
  }

  return (
    <div data-testid="promo-historial-tab">
      <div className="flex flex-wrap gap-3 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="historial-q" placeholder="Buscar usuario…" className="flex-1 min-w-[180px] py-2 px-3 rounded-xl border border-slate-200 text-sm" />
        <select value={origen} onChange={(e) => setOrigen(e.target.value)} data-testid="historial-origen" className="py-2 px-3 rounded-xl border border-slate-200 text-sm">
          <option value="">Todos los orígenes</option>
          <option value="CORTESIA">Cortesía</option>
          <option value="EMBAJADOR">Embajador</option>
        </select>
        <select value={estado} onChange={(e) => setEstado(e.target.value)} data-testid="historial-estado" className="py-2 px-3 rounded-xl border border-slate-200 text-sm">
          <option value="">Todos los estados</option>
          <option value="activo">Activo</option>
          <option value="finalizado">Finalizado</option>
        </select>
      </div>

      {loading ? <div className="text-slate-400 py-10 text-center"><Loader2 className="mx-auto animate-spin" /></div>
        : data.items.length === 0 ? <p className="text-sm text-slate-400 py-10 text-center bg-white rounded-2xl border border-slate-100">Sin beneficios registrados.</p>
        : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Usuario</th>
                  <th className="text-left px-4 py-3 font-bold">Beneficio</th>
                  <th className="text-left px-4 py-3 font-bold">Inicio</th>
                  <th className="text-left px-4 py-3 font-bold">Vence</th>
                  <th className="text-left px-4 py-3 font-bold">Estado</th>
                  <th className="text-right px-4 py-3 font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((b) => (
                  <tr key={b.id} className="border-t border-slate-50" data-testid={`promo-hist-${b.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-800">{b.user_nombre || b.user_email}</p>
                      {b.motivo && <p className="text-xs text-slate-400">{b.motivo}</p>}
                    </td>
                    <td className="px-4 py-3"><div className="flex items-center gap-1.5"><PlanChip plan={b.plan} /><OrigenChip origen={b.origen} label={b.origen_label} /></div></td>
                    <td className="px-4 py-3 text-slate-500">{fmt(b.fecha_inicio)}</td>
                    <td className="px-4 py-3 text-slate-500">{fmt(b.fecha_vencimiento)}</td>
                    <td className="px-4 py-3"><EstadoChip vigente={b.vigente} estado={b.estado} /></td>
                    <td className="px-4 py-3 text-right">
                      {b.vigente && !b.solo_lectura ? (
                        <div className="inline-flex gap-1.5">
                          <button onClick={() => accion(renovarBeneficio, b.id, "Renovado.")} data-testid={`promo-hist-renovar-${b.id}`} className="text-xs font-bold text-brand-blue hover:underline inline-flex items-center gap-1"><RotateCcw size={12} /> Renovar</button>
                          <button onClick={() => accion(finalizarBeneficio, b.id, "Finalizado.")} data-testid={`promo-hist-finalizar-${b.id}`} className="text-xs font-bold text-brand-red hover:underline inline-flex items-center gap-1"><XCircle size={12} /> Finalizar</button>
                        </div>
                      ) : <span className="text-xs text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {/* Ubicación B · Licencias de Programa: acción Gestionar (mismo gestor que Licencias de Programa) */}
      <div className="mt-8" data-testid="promo-hist-licencias-seccion">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={16} style={{ color: GOLD }} />
          <h3 className="font-bold text-slate-900">Licencias de Programa</h3>
        </div>
        {lics.length === 0 ? (
          <p className="text-sm text-slate-400 py-6 text-center bg-white rounded-2xl border border-slate-100">Sin licencias de programa registradas.</p>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-soft overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3 font-bold">Usuario</th>
                  <th className="text-left px-4 py-3 font-bold">Programa</th>
                  <th className="text-left px-4 py-3 font-bold">Vence</th>
                  <th className="text-left px-4 py-3 font-bold">Estado</th>
                  <th className="text-right px-4 py-3 font-bold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lics.map((l) => {
                  const st = { ACTIVA: "bg-emerald-100 text-emerald-700", VENCIDA: "bg-amber-100 text-amber-700", REVOCADA: "bg-rose-100 text-rose-700", SIN_LICENCIA: "bg-slate-100 text-slate-500" }[l.estado] || "bg-slate-100 text-slate-500";
                  return (
                    <tr key={`${l.user_id}-${l.programa_slug}`} className="border-t border-slate-50" data-testid={`promo-hist-lic-${l.programa_slug}`}>
                      <td className="px-4 py-3"><p className="font-semibold text-slate-800">{l.user_nombre || l.user_email || l.user_id}</p></td>
                      <td className="px-4 py-3 text-slate-600">{l.programa_nombre}</td>
                      <td className="px-4 py-3 text-slate-500">{fmt(l.fecha_vencimiento)}</td>
                      <td className="px-4 py-3"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${st}`}>{l.estado === "SIN_LICENCIA" ? "SIN LICENCIA" : l.estado}</span></td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setGestor(l)} data-testid={`promo-hist-lic-gestionar-${l.programa_slug}`} className="text-xs font-bold text-brand-blue hover:underline inline-flex items-center gap-1"><KeyRound size={12} /> Gestionar</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <GestorLicenciaModal
        open={!!gestor}
        userId={gestor?.user_id}
        slug={gestor?.programa_slug}
        nombre={gestor?.programa_nombre}
        onClose={() => setGestor(null)}
        onChanged={() => setLicRefresh((k) => k + 1)}
      />
    </div>
  );
}

const CampoLic = ({ label, children }) => (
  <label className="block">
    <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

// Suma meses calendario (espejo client-side de _add_months del backend, solo para previsualizar).
function addMonths(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, last));
  return d;
}

function LicenciasProgramaTab() {
  const [usuario, setUsuario] = useState(null);
  const [programas, setProgramas] = useState([]);
  const [slug, setSlug] = useState("");
  const [meses, setMeses] = useState(3);
  const [diasAdic, setDiasAdic] = useState(0);
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [busy, setBusy] = useState(false);
  const [editandoPrecios, setEditandoPrecios] = useState(false);
  const [precioForm, setPrecioForm] = useState({ m1: "", m3: "", m6: "", m12: "" });
  const [resultado, setResultado] = useState(null);
  const [licRefresh, setLicRefresh] = useState(0);

  const cargarProgramas = () => fetchProgramasLicenciablesAdmin().then((d) => {
    setProgramas(d || []);
    setSlug((prev) => prev || (d && d.length ? d[0].slug : ""));
  }).catch(() => setProgramas([]));

  useEffect(() => { cargarProgramas(); }, []);

  const prog = programas.find((p) => p.slug === slug) || null;
  const precios = prog?.precios || null;
  const solesSel = precios ? precios[`m${meses}`]?.soles : null;

  const activacion = new Date();
  const vencPreview = addMonths(activacion, Number(meses));
  if (diasAdic > 0) vencPreview.setDate(vencPreview.getDate() + Number(diasAdic));

  async function otorgar() {
    if (!usuario) { toast.error("Selecciona un usuario."); return; }
    if (!slug) { toast.error("Selecciona un programa."); return; }
    setBusy(true);
    try {
      const r = await otorgarLicenciaAdmin({
        user_id: usuario.id, programa_slug: slug, duracion_meses: Number(meses),
        dias_adicionales: Number(diasAdic) || 0, motivo,
      });
      setResultado(r.licencia);
      setLicRefresh((k) => k + 1);
      toast.success("Licencia otorgada como beneficio (origen ADMINISTRADOR).");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo otorgar."); }
    finally { setBusy(false); }
  }

  async function guardarPrecios() {
    if (!prog) return;
    const body = { m1: Math.round(parseFloat(precioForm.m1) * 100), m3: Math.round(parseFloat(precioForm.m3) * 100), m6: Math.round(parseFloat(precioForm.m6) * 100), m12: Math.round(parseFloat(precioForm.m12) * 100) };
    if ([body.m1, body.m3, body.m6, body.m12].some((v) => !Number.isInteger(v) || v <= 0)) { toast.error("Precios en soles válidos (> 0)."); return; }
    setBusy(true);
    try {
      await actualizarPreciosPrograma(prog.id, body);
      toast.success("Precios actualizados (solo futuras licencias).");
      setEditandoPrecios(false);
      await cargarProgramas();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron guardar los precios."); }
    finally { setBusy(false); }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 max-w-2xl" data-testid="promo-licencias-tab">
      <h2 className="font-bold text-slate-900 mb-1">Otorgar Licencia — Programa Especializado</h2>
      <p className="text-xs text-slate-500 mb-4">Beneficio sin Mercado Pago · independiente de la membresía · trazabilidad en auditoría.</p>

      <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Usuario</label>
      <BuscadorUsuario onSelect={setUsuario} seleccionado={usuario} testid="lic-user" />

      <div className="grid grid-cols-2 gap-4 mt-5">
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Programa Especializado</label>
          <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="lic-programa" className="w-full py-2.5 px-3 rounded-xl border border-slate-200 text-sm">
            {programas.length === 0 && <option value="">Sin programas licenciables</option>}
            {programas.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase mb-1.5">Duración</label>
          <select value={meses} onChange={(e) => setMeses(parseInt(e.target.value, 10))} data-testid="lic-duracion" className="w-full py-2.5 px-3 rounded-xl border border-slate-200 text-sm">
            <option value={1}>1 mes{precios ? ` · S/ ${precios.m1?.soles}` : ""}</option>
            <option value={3}>3 meses{precios ? ` · S/ ${precios.m3?.soles}` : ""}</option>
            <option value={6}>6 meses{precios ? ` · S/ ${precios.m6?.soles}` : ""}</option>
            <option value={12}>12 meses{precios ? ` · S/ ${precios.m12?.soles}` : ""}</option>
          </select>
        </div>
      </div>

      {precios && (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-xl bg-slate-50 border border-slate-100 px-3 py-2">
          <span className="text-sm text-slate-600">Precio vigente: <b className="text-slate-900">S/ {solesSel}</b></span>
          <button onClick={() => { setPrecioForm({ m1: (precios.m1.centimos / 100).toFixed(2), m3: (precios.m3.centimos / 100).toFixed(2), m6: (precios.m6.centimos / 100).toFixed(2), m12: (precios.m12.centimos / 100).toFixed(2) }); setEditandoPrecios((v) => !v); }}
            data-testid="lic-editar-precios" className="text-xs font-bold text-brand-blue hover:underline inline-flex items-center gap-1"><Pencil size={12} /> Editar precios</button>
        </div>
      )}
      {editandoPrecios && (
        <div className="mt-2 grid grid-cols-4 gap-2" data-testid="lic-precios-form">
          {["m1", "m3", "m6", "m12"].map((k) => (
            <label key={k} className="text-xs font-semibold text-slate-500">{k.toUpperCase()} (S/)
              <input type="number" step="0.01" min="0" value={precioForm[k]} onChange={(e) => setPrecioForm((s) => ({ ...s, [k]: e.target.value }))} data-testid={`lic-precio-${k}`} className="mt-1 w-full py-2 px-2 rounded-lg border border-slate-200 text-sm" />
            </label>
          ))}
          <button onClick={guardarPrecios} disabled={busy} data-testid="lic-precios-guardar" className="col-span-4 py-2 rounded-lg bg-brand-navy hover:bg-brand-blue text-white text-sm font-bold disabled:opacity-60">Guardar precios (solo futuras)</button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mt-4">
        <CampoLic label="Días adicionales de cortesía">
          <input type="number" min="0" value={diasAdic} onChange={(e) => setDiasAdic(Math.max(0, parseInt(e.target.value || "0", 10)))} data-testid="lic-dias-adicionales" className="w-full py-2.5 px-3 rounded-xl border border-slate-200 text-sm" />
        </CampoLic>
        <CampoLic label="Motivo">
          <select value={motivo} onChange={(e) => setMotivo(e.target.value)} data-testid="lic-motivo" className="w-full py-2.5 px-3 rounded-xl border border-slate-200 text-sm">
            {MOTIVOS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </CampoLic>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-2.5" data-testid="lic-fecha-activacion">
          <p className="text-[11px] font-bold uppercase text-slate-400">Activación</p>
          <p className="font-bold text-slate-900">{fmt(activacion.toISOString())}</p>
        </div>
        <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-3 py-2.5" data-testid="lic-fecha-vencimiento">
          <p className="text-[11px] font-bold uppercase text-emerald-600">Vence (estimado)</p>
          <p className="font-bold text-emerald-800">{fmt(vencPreview.toISOString())}</p>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-slate-400">Si el usuario ya tiene licencia vigente, el backend extiende desde el vencimiento actual (no reduce tiempo). La fecha definitiva se muestra al confirmar.</p>

      <button onClick={otorgar} disabled={busy || !usuario || !slug} data-testid="lic-otorgar"
        className="mt-5 w-full py-3 rounded-xl text-white font-bold inline-flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: GOLD }}>
        {busy ? <Loader2 size={17} className="animate-spin" /> : <KeyRound size={17} />} Confirmar otorgamiento
      </button>

      {resultado && (
        <div className="mt-5 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="lic-resultado">
          <p className="font-bold text-emerald-800 inline-flex items-center gap-2"><KeyRound size={16} /> Licencia otorgada</p>
          <div className="mt-2 text-sm text-emerald-900 grid grid-cols-2 gap-2">
            <span>Activación: <b>{fmt(resultado.fecha_activacion)}</b></span>
            <span>Vencimiento: <b>{fmt(resultado.fecha_vencimiento)}</b></span>
            <span>Duración: <b>{resultado.duracion_meses} meses</b></span>
            <span>Días adicionales: <b>{resultado.dias_adicionales}</b></span>
            <span>Origen: <b>ADMINISTRADOR</b></span>
            <span>Precio ref.: <b>S/ {resultado.precio_referencia_soles}</b></span>
          </div>
        </div>
      )}

      {usuario && slug && (
        <LicenciaActual
          key={`${usuario.id}-${slug}-${licRefresh}`}
          userId={usuario.id}
          slug={slug}
          nombre={prog?.name || slug}
          onChanged={() => setLicRefresh((k) => k + 1)}
        />
      )}
    </div>
  );
}

